import { useCallback, useEffect, useRef, useState } from 'react';
import type { Conn } from '../conn';
import { getUsageEvents, type UsageEvent } from '../api';

export default function MonitoringPage({ conn }: { conn: Conn }) {
  const [events, setEvents] = useState<UsageEvent[]>([]);
  const [err, setErr] = useState('');
  const [live, setLive] = useState(false);
  const timer = useRef<number | null>(null);

  const load = useCallback(async () => {
    if (!conn.apiKey) { setErr('Enter an API key (top bar).'); return; }
    setErr('');
    try { setEvents(await getUsageEvents(conn.baseUrl, conn.apiKey, 200)); }
    catch (e) { setErr(String(e)); }
  }, [conn.baseUrl, conn.apiKey]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (live) { timer.current = window.setInterval(load, 4000); }
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [live, load]);

  const totalCost = events.reduce((s, e) => s + e.cost_usd, 0);
  const totalTok = events.reduce((s, e) => s + e.input_tokens + e.output_tokens, 0);

  return (
    <div className="page">
      <header className="page-head">
        <h1>Monitoring</h1>
        <div className="row">
          <label className="muted small"><input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} /> live tail</label>
          <button onClick={load}>Refresh</button>
        </div>
      </header>
      {err && <p className="err">{err}</p>}
      <p className="muted small">Per-call records from the tracing layer. OpenTelemetry span trees + token/cost via Phoenix/Langfuse land with the monitoring backend (design §7); this is the per-call feed available today.</p>

      <div className="cards">
        <div className="card"><div className="card-k">Calls</div><div className="card-v">{events.length}</div></div>
        <div className="card"><div className="card-k">Tokens</div><div className="card-v">{totalTok}</div></div>
        <div className="card"><div className="card-k">Cost (est.)</div><div className="card-v">${totalCost.toFixed(4)}</div></div>
        <div className="card"><div className="card-k">Tail</div><div className="card-v">{live ? '● live' : 'paused'}</div></div>
      </div>

      <table className="grid">
        <thead><tr><th>time</th><th>provider</th><th>model</th><th>in</th><th>out</th><th>cost</th></tr></thead>
        <tbody>
          {events.length === 0 && <tr><td colSpan={6} className="muted">No calls yet — run an agent.</td></tr>}
          {events.map((e) => (
            <tr key={e.id}>
              <td className="small">{e.ts?.replace('T', ' ').slice(0, 19)}</td>
              <td>{e.provider}</td>
              <td className="small"><code>{e.model}</code></td>
              <td>{e.input_tokens}</td>
              <td>{e.output_tokens}</td>
              <td>${e.cost_usd.toFixed(5)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
